import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { FC, Fragment, MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { IResourceClient } from '@bom-nextgen-keycloak/models';
import {
  HeadCell,
  OrderTable,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  EnhancedTableHead,
  Loading,
  NotFound2,
  PaginationPro,
  StyledTextEllipsis,
  TableContainer,
  TableRowSkeleton,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  getComparator,
  stableSort,
} from '@bom-nextgen-keycloak/web/shared/util';

type ResourceTableSortKey = Pick<IResourceClient, '_id' | 'name' | 'type'>;

type ResourceTableProps = {
  canDelete?: boolean;
  canEdit?: boolean;
  data: IResourceClient[];
  enabledDelete?: boolean;
  enabledEdit?: boolean;
  isFetched?: boolean;
  isFetching?: boolean;
  isFilter?: boolean;
  isLoading?: boolean;
  isProcessing?: boolean;
  isSearch?: boolean;
  isSearching?: boolean;
  onDelete: (resource: IResourceClient) => void;
  onPageChange?: (offset: number) => void;
  onStepChange?: (event: SelectChangeEvent<unknown>) => void;
  paginationOffset?: number;
  paginationStep?: number;
  totalCount?: number | undefined;
  totalRecord: number;
  wasSearch?: boolean;
};

const headCells: HeadCell<keyof ResourceTableSortKey>[] = [
  {
    disablePadding: false,
    id: 'name',
    label: 'Name',
    numeric: false,
    sortKey: 'name',
  },
  {
    disablePadding: false,
    id: 'type',
    label: 'Type',
    numeric: false,
    sortKey: 'type',
  },
  {
    disablePadding: false,
    id: 'actions',
    label: 'Actions',
    numeric: false,
    sortKey: '_id',
  },
];

const ResourceTable2: FC<ResourceTableProps> = ({
  canDelete = false,
  canEdit = false,
  data,
  enabledDelete = true,
  enabledEdit = true,
  isFetched = false,
  isFetching = true,
  isFilter = false,
  isLoading = true,
  isProcessing = true,
  isSearch = false,
  isSearching = false,
  onDelete,
  onPageChange,
  onStepChange,
  paginationOffset = 0,
  paginationStep = 10,
  totalCount = undefined,
  totalRecord = 0,
  wasSearch = false,
}) => {
  const [order, setOrder] = useState<OrderTable>('desc');
  const [orderBy, setOrderBy] = useState<keyof ResourceTableSortKey>('name');

  const isEmpty = data.length === 0;
  const columnLength = headCells.length;
  const showSkeleton = isProcessing || wasSearch;
  const showHeader =
    isProcessing || wasSearch || (!isProcessing && totalRecord > 0);
  const showDeleteButton = canDelete && enabledDelete;
  const showEditButton = canEdit && enabledEdit;

  const handleSort = (
    event: MouseEvent<unknown>,
    property: keyof ResourceTableSortKey
  ) => {
    const isAsc = orderBy === property && order === 'asc';

    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Paper>
      {isSearching ? (
        <Loading title="Searching..." />
      ) : (
        <Fragment>
          <TableContainer>
            <Table
              aria-label="resource table"
              aria-labelledby="tableTitle"
              stickyHeader
            >
              {showHeader && (
                <EnhancedTableHead
                  headCells={headCells}
                  onRequestSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                />
              )}
              <TableBody>
                <TableRowSkeleton
                  cellLength={columnLength}
                  loading={showSkeleton}
                >
                  {!isEmpty ? (
                    stableSort<IResourceClient>(
                      data,
                      getComparator(order, orderBy)
                    ).map((row) => {
                      const { _id, name, type } = row;

                      return (
                        <TableRow key={_id}>
                          <TableCell title={name}>
                            <StyledTextEllipsis>{name}</StyledTextEllipsis>
                          </TableCell>
                          <TableCell title={type}>
                            <StyledTextEllipsis>{type}</StyledTextEllipsis>
                          </TableCell>
                          <TableCell>
                            {showEditButton && (
                              <Link to={`/resources/${_id}`}>
                                <Tooltip title="Edit resource">
                                  <IconButton aria-label="edit" size="small">
                                    <EditIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Link>
                            )}

                            {showDeleteButton && (
                              <Tooltip title="Delete resource">
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => onDelete(row)}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columnLength}>
                        <NotFound2 />
                      </TableCell>
                    </TableRow>
                  )}
                </TableRowSkeleton>
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationPro
            isFetched={isFetched}
            isFetching={isFetching}
            isFilter={isFilter}
            isLoading={isLoading}
            isProcessing={isProcessing}
            isSearch={isSearch}
            isSearching={isSearching}
            onPageChange={onPageChange}
            onStepChange={onStepChange}
            paginationOffset={paginationOffset}
            paginationStep={paginationStep}
            totalCount={totalCount}
            totalRecord={totalRecord}
            wasSearch={wasSearch}
          />
        </Fragment>
      )}
    </Paper>
  );
};

export { ResourceTable2 };
